/**
 * What the web must still do to a server payload before a screen can render it.
 * OWNER: web-frontend-engineer.
 *
 * THIS FILE USED TO RENAME FIELDS. IT NO LONGER DOES.
 *
 * The backend engineer's C-2 was right and my integration note was wrong:
 * `commissionTypeLabel`, `stageHistory`, `clientVisibleNotes`, `daysInState`,
 * `id`, `weight_gsm`, `width_cm` are `CLIENT_ORDER_FIELDS` and
 * `CLIENT_CLOTH_FIELDS` in `packages/core/src/serialization/client-payloads.ts`
 * — the architect's own O-7 allow-list, from which the client payload is BUILT
 * BY PICKING. A rename here was a second vocabulary for the same fact, and the
 * view models now carry the contract's names verbatim.
 *
 * What is left is only what a server payload genuinely does not carry:
 *
 *   1. FACETS — `API-FABRICS-LIST` returns cloths, not the facet lists the
 *      filters need. "Facet values render only when present in the current
 *      result set" (FLOWS_SPEC §5.1), so they are computed from the result set.
 *   2. ASSET PATHS — the capture lives in `packages/fabric` and
 *      `public/fabric/**`, not in the API. The cloth and its picture are joined
 *      here.
 *   3. STAGE DERIVATIONS — `StatusTimeline` states who confirmed, who cut and
 *      when it was last touched. Those are read off `stageHistory`; the server
 *      is not asked to precompute a presentation.
 *   4. THE CUT SHEET'S BLOCK NAMES — `block0_header … block6_clientWords`
 *      follows OPS_SPEC §3.2's own structure because `packages/core` declares
 *      no cut-sheet type (BACKEND_NOTES C-1). THIS ADAPTER STAYS until the
 *      architect names one, and its deletion is a one-line change when he does.
 *   5. REVIEW'S COMPOSITION — `API-REVIEW-GET` carries the quote and the
 *      binding but not the cloth's own facts, and `[T-58]` requires the cloth
 *      stated in full on that page.
 *
 * It invents nothing: a field the server does not send stays `null`, and the
 * screen renders its designed absent state.
 */

import { colourStatementInputs, fabricMaterial } from '@pt/fabric';
import { clientMillName } from '@pt/core';
import { t } from '@pt/copy';
import { dateInWords } from '@pt/view';
import type {
  AtelierOrderPayload,
  ClothDetailPayload,
  ClothSummary,
  CutSheetPayload,
  FabricListPayload,
  HomePayload,
  OrderPayload,
  ReviewPayload,
} from './contracts.ts';
import { optionalLabel, slotFreeLabel } from './label.ts';

type Raw = Record<string, unknown>;

const str = (value: unknown, fallback = ''): string =>
  typeof value === 'string' ? value : fallback;
const num = (value: unknown, fallback = 0): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;
const arr = (value: unknown): readonly Raw[] => (Array.isArray(value) ? (value as Raw[]) : []);
/** A table cell: the value as text, or the empty cell — never the word `null`. */
const cell = (value: unknown): string =>
  value === null || value === undefined ? '' : String(value);

/* ------------------------------------------------------------------ cloth */

/**
 * The cloth as the contract sends it, plus the capture the API does not carry.
 * `macroSwatchPath` and `patternRepeatCm` come from `packages/fabric`, which is
 * where the capture pipeline's output lives; an absent capture stays `null` and
 * renders `ST-BOOK-IMAGE-MISSING` rather than a broken image.
 */
export function withCapture(cloth: Raw): ClothSummary {
  const clothId = str(cloth['id']);
  const colour = colourStatementInputs(clothId);
  const material = fabricMaterial(clothId);
  // C1, 5 September 2026. The engineer's `note` is not a client field: the
  // server no longer sends it, and should an older payload, it is dropped
  // here rather than carried into the view model by the spread below.
  const fields: Raw = { ...cloth };
  delete fields['note'];
  return {
    ...(fields as unknown as ClothSummary),
    // C1, 5 September 2026. The server already omits a mill it cannot name;
    // this applies the SAME rule (`clientMillName`, @pt/core) on this end, so a
    // placeholder name cannot reach a screen from any payload, and an absent
    // field reads as `null` rather than `undefined`.
    mill: clientMillName(cloth['mill']),
    clientNote: str(cloth['clientNote']) || null,
    macroSwatchPath: colour?.macroSwatchPath ?? null,
    tilePath: material?.tile?.path ?? null,
    patternRepeatCm:
      typeof cloth['patternRepeatCm'] === 'number'
        ? (cloth['patternRepeatCm'] as number)
        : (material?.patternRepeatCm ?? null),
  };
}

/** The nine house colour families and the word each matches in a `colourName`. */
export const COLOUR_FAMILIES: readonly { id: string; term: string }[] = [
  { id: 'navy', term: 'navy' },
  { id: 'grey', term: 'grey' },
  { id: 'charcoal', term: 'charcoal' },
  { id: 'black_midnight', term: 'midnight' },
  { id: 'brown_tan', term: 'brown' },
  { id: 'green', term: 'green' },
  { id: 'blue_other', term: 'blue' },
  { id: 'natural_ivory', term: 'natural' },
  { id: 'stone_fawn', term: 'stone' },
];

export function colourFamilyTerm(id: string): string {
  return COLOUR_FAMILIES.find((family) => family.id === id)?.term ?? id;
}

export function adaptFabricList(raw: Raw): FabricListPayload {
  const cloths = arr(raw['cloths']).map(withCapture);
  const excluded = arr(raw['excluded']).map(withCapture);
  const everything = [...cloths, ...excluded];
  const context = raw['inCommissionContext'] as Raw | null | undefined;

  return {
    cloths,
    excluded,
    currency: str(raw['currency'], 'USD'),
    placeholderCatalogue: raw['placeholderCatalogue'] === true,
    commissionTypeLabel:
      context == null ? null : str(context['commissionTypeLabel']) || null,
    // Facet values render ONLY when present in the current result set.
    facets: {
      seasonClass: [...new Set(everything.map((cloth) => cloth.seasonClass))].filter((s) => s !== ''),
      occasion: [...new Set(everything.flatMap((cloth) => cloth.occasions))],
      weave: [...new Set(everything.map((cloth) => cloth.weave))].filter((w) => w !== ''),
      // `colour` filters as a SUBSTRING of `colourName`; the catalogue names
      // nine families. A family shows only where a cloth carries its word.
      colour: COLOUR_FAMILIES.filter(({ term }) =>
        everything.some((cloth) => cloth.colourName.toLowerCase().includes(term)),
      ).map(({ id }) => id),
    },
    // ST-BOOK-HOUSE renders only when ≥2 slots fill; slot A is not built in R1
    // (FLOWS_SPEC §13.2), and the server offers no curation, so it stays absent.
    curation: [],
  };
}

export function adaptClothDetail(raw: Raw): ClothDetailPayload {
  const cloth = withCapture(raw);
  return {
    ...cloth,
    // RULING-007: the house note renders only if authored — and, since C1
    // (5 September 2026), only if authored FOR THE CLIENT. `raw.note` is the
    // engineer's and is deliberately not read here.
    houseNote: cloth.clientNote,
    nearestAlternative: null,
    millWeavesStill: false,
  };
}

/* ------------------------------------------------------------------- home */

export function adaptHome(raw: Raw): HomePayload {
  const tailor = raw['tailor'] as Raw | null | undefined;
  return {
    ...(raw as unknown as HomePayload),
    // The server sends no house contact. `EL-HUMAN` then names the master
    // tailor who holds the record — a person with a role, never `support@`.
    contact:
      tailor == null
        ? null
        : {
            displayName: str(tailor['displayName']),
            role: t('global.role.master_tailor'),
            address: str(tailor['address']),
          },
  };
}

/* ------------------------------------------------------------------ order */

/**
 * `StatusTimeline` states who confirmed it, who cut it and when it was last
 * touched. Those are read off `stageHistory` here rather than asked of the
 * server, because they are a presentation of the history, not new facts.
 */
export function adaptOrder(raw: Raw): OrderPayload {
  const history = arr(raw['stageHistory']);
  const at = (state: string) => history.find((entry) => str(entry['state']) === state);
  const last = history[history.length - 1];

  return {
    ...(raw as unknown as OrderPayload),
    confirmedBy: str(at('confirmed')?.['byName']) || null,
    cutAt: str(at('cut')?.['at']) || null,
    cutBy: str(at('cut')?.['byName']) || null,
    haltedBy: str(at('halted')?.['byName']) || null,
    fittingArrangedBy: str(at('fitting')?.['byName']) || null,
    lastEventAt: str(last?.['at'], str(raw['placedAt'])),
  };
}

/* ----------------------------------------------------------------- review */

export function adaptReview(raw: Raw, cloth: ClothSummary | null): ReviewPayload {
  const binding = (raw['measurementBinding'] as Raw | undefined) ?? {};
  const details = (raw['details'] as Raw | undefined) ?? {};
  const acks = arr(raw['acknowledgements']);
  const colour = acks.find((ack) => str(ack['acknowledgementId']) === 'ack.colour_on_screen');
  const boundFabricId = str(colour?.['bindsToFabricId']) || null;
  const clothId = cloth?.id ?? '';

  return {
    draftId: str(raw['draftId']),
    quote: raw['quote'] as ReviewPayload['quote'],
    configuration: (raw['configuration'] ?? {}) as ReviewPayload['configuration'],
    options: (raw['options'] ?? []) as ReviewPayload['options'],
    cloth,
    record: {
      recordId: str(binding['recordId']),
      authorName: str(binding['authoredByName']),
      takenAt: str(binding['takenAt']),
      contentHash: str(binding['contentHash']),
    },
    acknowledgements: acks
      .filter((ack) => str(ack['acknowledgementId']) !== 'ack.colour_on_screen')
      .map((ack) => ({
        acknowledgementId: str(ack['acknowledgementId']),
        version: num(ack['version'], 1),
        text: str(ack['text']),
        copyKey: null,
        acceptedAt: str(ack['acceptedAt']) || null,
        blocksOrder: ack['blocksOrder'] !== false,
      })),
    colourAck: {
      acknowledgementId: 'ack.colour_on_screen',
      version: num(colour?.['version'], 3),
      // [T-46]: the acknowledgement's text is the DOMAIN OWNER'S, shown
      // verbatim and stored verbatim. Round 1.3.1 settled BACKEND_NOTES D-1 at
      // source — the sentence now claims no ΔE and `deltaEShown` is null,
      // which is what [T-39] requires while the book is unmeasured. The web
      // no longer substitutes its own sentence for it.
      text: str(colour?.['text']),
      // `textSource` deliberately NOT lifted (ruling R-16). The server strips it
      // from the client ack (routes/client/drafts.ts) — it is staff provenance —
      // and the client shape no longer carries it. Reading it here would have
      // been the adapter quietly reintroducing the leak the projection closed.
      boundFabricId,
      deltaEShown:
        typeof colour?.['deltaEShown'] === 'number' ? (colour['deltaEShown'] as number) : null,
      deltaEBasis: str(colour?.['deltaEBasis']) || null,
      pathChosen: null,
      // P-FAB-05: an acceptance bound to a different cloth is void, and the
      // block is presented fresh for the cloth actually being cut.
      voided: boundFabricId !== null && clothId !== '' && boundFabricId !== clothId,
    },
    details: {
      deliveryAddress: str(details['deliveryAddress']),
      contact: str(details['contact']),
      fittingPreference: str(details['fittingPreference']),
      onFile:
        details['deliveryAddress'] != null ||
        details['contact'] != null ||
        details['fittingPreference'] != null,
    },
    tailor: { displayName: str(binding['authoredByName']), role: 'master_tailor' },
    // D-030(c): the third fact renders only while the ask action ships. It
    // does — `SCR-ORDER-ASK` is built and the server enforces the window.
    askActionShips: true,
    revalidated: null,
    recordSuperseded: null,
  };
}

/* -------------------------------------------------------------- cut sheet */

/**
 * BACKEND_NOTES C-1: `packages/core` declares no cut-sheet type, so the server
 * follows OPS_SPEC §3.2's own Block 0–7 names. This is the one adapter with a
 * standing reason to exist; it goes the day the architect names a type.
 *
 * A field with no catalogue key is DROPPED, never labelled here (D-026(b)), and
 * a key that carries a slot is not a label — using one would print
 * `{authorName}` on a document a suit is cut from.
 */
const CUTSHEET_LABEL: Readonly<Record<string, string>> = {
  orderRef: 'queue.column.order',
  /* 5 September 2026, finding C2 (P0). This row is the commission — "Two-piece
     suit" — not the order's stage; `orddet.header.stage` put "Stage: Two-piece
     suit" on paper while the real stage row above the sheet said "Halted". The
     catalogue's own word for the commission-type fact is the option's label. */
  commissionTypeLabel: 'config.option.commission.type.label',
  clientName: 'orddet.header.client',
  clientRef: 'clients.column.ref',
  placedAt: 'orddet.header.placed',
  assignedMasterTailor: 'orddet.header.tailor',
  assignedCutter: 'orddet.header.cutter',
  fittingPreference: 'review.details.fitting.label',
  clothName: 'queue.column.cloth',
  takenAt: 'measnew.takenAt.label',
  method: 'measnew.method.label',
};

/**
 * 5 September 2026, finding C2 (P0): the master tailor's order page printed
 * `2026-06-08T10:00:00.000Z` and `in_person` on a document a suit is cut from.
 * Law 32(b): never an ISO timestamp, never a raw enum id on paper. Every value
 * the sheet prints is therefore FORMATTED BY KEY — a date through the one
 * `dateInWords` every surface shares (@pt/view, ruling R-9), an enum through
 * the catalogue's own word for it, the same key `ProvenanceLine` renders the
 * same fact with (components/record.tsx). A value with no word is DROPPED,
 * never echoed — the same omit-rather-than-invent rule as the labels above.
 */
const CUTSHEET_DATE_KEYS: ReadonlySet<string> = new Set(['placedAt', 'takenAt']);

const CUTSHEET_ENUM_PREFIX: Readonly<Record<string, string>> = {
  method: 'el.provenance.method.',
};

function cutSheetValue(key: string, value: string | number | boolean): string | null {
  if (CUTSHEET_DATE_KEYS.has(key)) {
    const words = dateInWords(String(value));
    return words === '' ? null : words;
  }
  const prefix = CUTSHEET_ENUM_PREFIX[key];
  if (prefix !== undefined) return optionalLabel(`${prefix}${String(value)}`);
  const text = String(value).trim();
  return text === '' ? null : text;
}

function blockRows(block: Raw): readonly { label: string; value: string }[] {
  const rows: { label: string; value: string }[] = [];
  for (const [key, raw] of Object.entries(block)) {
    if (raw === null || raw === undefined || typeof raw === 'object') continue;
    if (typeof raw !== 'string' && typeof raw !== 'number' && typeof raw !== 'boolean') continue;
    const labelKey = CUTSHEET_LABEL[key];
    if (labelKey === undefined) continue;
    const label = slotFreeLabel(labelKey);
    if (label === null) continue;
    // An empty cell is not a fact. A row prints only when it has a value.
    const value = cutSheetValue(key, raw);
    if (value === null) continue;
    rows.push({ label, value });
  }
  return rows;
}

/**
 * The figure the metreage sentence carries, or `null` when the sheet has none.
 * The server writes `requiredMetreage` (domain/cutsheet.ts block 4); the two
 * older names are kept for a sheet frozen before that name settled. A figure
 * that is not a positive quantity of cloth is no figure — the component then
 * prints NOTHING rather than "0.0" after a sentence (finding C2, 5 Sept 2026):
 * a figure never leaves the page without its basis, and a basis never stands
 * over a figure that is not there.
 */
function metreageFigure(cloth: Raw): number | null {
  for (const key of ['requiredMetreage', 'clothRequirementMetres', 'metresRequired']) {
    const value = cloth[key];
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) return value;
  }
  return null;
}

const ASYMMETRY_PAIRS: readonly (readonly [string, string])[] = [
  ['half_back_left', 'half_back_right'],
  ['sleeve_length_left', 'sleeve_length_right'],
  ['shoulder_slope_left_deg', 'shoulder_slope_right_deg'],
];

export function adaptCutSheet(raw: Raw, appendix: readonly Raw[]): CutSheetPayload | null {
  if (raw == null) return null;
  const header = (raw['block0_header'] as Raw | undefined) ?? {};
  const record = (raw['block2_record'] as Raw | undefined) ?? {};
  const cloth = (raw['block4_cloth'] as Raw | undefined) ?? {};
  const clientWords = (raw['block6_clientWords'] as Raw | undefined) ?? {};
  const bodyValues = arr(record['bodyValues']);

  /* 5 September 2026, finding C10: the sheet printed "When fittings suit you ·
     At the shop, weekday afternoons" TWICE — the server writes the fitting
     preference into block 1 (the people) and again into block 5 (the atelier's
     own block), and each block labelled it with the same key. A fact is stated
     once on a document a suit is cut from; the first block that carries it
     keeps it, and a later block repeating the same label AND the same value is
     not printed again. Two different values under one label would both print
     — that would be a real disagreement, and hiding it is not this rule's job. */
  /* AMENDED the same day (verifier): the header (block 0) and the people
     (block 1) both carry the client's name by the server's design, and the
     people block must name the client — so blocks 0 and 1 print whole and
     seed the set; the rule drops repeats from block 2 onward only. */
  const printed = new Set<string>();
  const onceOnly = (blockIndex: number) => (row: { label: string; value: string }): boolean => {
    const fact = `${row.label}\u001f${row.value}`;
    if (blockIndex <= 1) { printed.add(fact); return true; }
    if (printed.has(fact)) return false;
    printed.add(fact);
    return true;
  };

  const blocks = (
    [
      ['block0_header', 'cutsheet.block0'],
      ['block1_people', 'cutsheet.block1'],
      ['block2_record', 'cutsheet.block2'],
      ['block3_commission', 'cutsheet.block3'],
      ['block4_cloth', 'cutsheet.block4'],
      ['block5_commercial', 'cutsheet.block5'],
      ['block6_clientWords', 'cutsheet.block6'],
    ] as const
  )
    .map(([id, titleKey], blockIndex) => ({
      id,
      titleKey,
      rows: blockRows((raw[id] as Raw | undefined) ?? {}).filter(onceOnly(blockIndex)),
    }))
    // 5 September 2026, finding C2: a heading with nothing under it is not a
    // block, it is a promise the sheet did not keep. Three of the seven
    // printed that way. A block prints only when it has a row to print.
    .filter((block) => block.rows.length > 0);

  return {
    orderRef: str(header['orderRef']),
    clientName: str(header['clientName']),
    recordId: str(record['measurementRecordId']),
    contentHash: str(record['contentHash']),
    schemaVersion: str(header['rulesSchemaVersion']),
    blocks,
    derivedMeasures: bodyValues
      .filter((value) => value['finished'] != null)
      .map((value) => ({ label: str(value['label']), value: String(value['finished']) })),
    // The asymmetry block PRINTS EVEN WHEN LEFT AND RIGHT ARE EQUAL, so its
    // absence can never be mistaken for symmetry. Never averaged.
    //
    // 5 September 2026, finding C2: EQUAL is not ABSENT. A pair nobody measured
    // printed as its machine id — `shoulder_slope_left_deg` — over two empty
    // cells, and a one-sided pair printed the present side as the whole
    // difference. A row prints when it has a value on either side; the
    // difference is stated only when both sides were taken; a pair with a
    // value but no label in the record takes the catalogue's own word for the
    // field, and a pair with neither value nor word is not a row.
    asymmetry: ASYMMETRY_PAIRS.flatMap(([leftId, rightId]) => {
      const left = bodyValues.find((value) => str(value['id']) === leftId);
      const right = bodyValues.find((value) => str(value['id']) === rightId);
      const l = cell(left?.['value']);
      const r = cell(right?.['value']);
      if (l === '' && r === '') return [];
      const fieldLabel = str(left?.['label']) || optionalLabel(`meas.field.${leftId}.label`);
      if (fieldLabel === null) return [];
      const bothTaken = typeof left?.['value'] === 'number' && typeof right?.['value'] === 'number';
      return [
        {
          fieldLabel,
          left: l,
          right: r,
          difference:
            !bothTaken || l === r
              ? null
              : String(Math.abs(num(left?.['value']) - num(right?.['value']))),
        },
      ];
    }),
    overrides: [],
    clientNote: str(clientWords['clientNote']) || null,
    napDirectional: cloth['napDirectional'] === true,
    colourPlaceholder: cloth['placeholder'] === true,
    estimatedMetreage: metreageFigure(cloth),
    appendix: appendix.map((entry) => ({
      event: str(entry['event']),
      actorName: str(entry['actorDisplayName']),
      at: str(entry['at']),
    })),
  };
}

/* ------------------------------------------------------------ back office */

/**
 * `API-ATELIER-ORDER-GET` is a composite: the order, its frozen cut sheet, the
 * appendix and `allowedEvents` for this role. Flattening it is not a rename —
 * it is the one shape a single screen renders.
 */
export function adaptAtelierOrder(raw: Raw): AtelierOrderPayload {
  const order = (raw['order'] as Raw | undefined) ?? {};
  const bound = raw['boundRecord'] as Raw | null | undefined;
  const appendix = arr(raw['appendix']);

  return {
    ...(order as unknown as AtelierOrderPayload),
    // THE control set, verbatim. The front end holds no copy of the table.
    allowedEvents: (raw['allowedEvents'] ?? []) as AtelierOrderPayload['allowedEvents'],
    // The confirmation-gate panels (`SCR-CONFIRM`) read arrays and flags the
    // server only fills for a `placed` order it has assessed. A payload that
    // omits them is not a crash (clarity Finding 2): an absent array is empty,
    // an unstated integrity flag is "no fault found", an absent panel is null.
    // The panels render their designed absent states from these; nothing here
    // invents a completeness figure or a cloth line the server did not send.
    completeness: (raw['completeness'] ??
      (order as Raw)['completeness'] ??
      []) as AtelierOrderPayload['completeness'],
    advisories: (raw['advisories'] ??
      (order as Raw)['advisories'] ??
      []) as AtelierOrderPayload['advisories'],
    clothPanel: ((raw['clothPanel'] ??
      (order as Raw)['clothPanel'] ??
      null) as AtelierOrderPayload['clothPanel']),
    openClientRequest: ((raw['openClientRequest'] ??
      (order as Raw)['openClientRequest'] ??
      null) as AtelierOrderPayload['openClientRequest']),
    hashOk: (raw['hashOk'] ?? (order as Raw)['hashOk'] ?? true) as boolean,
    stockOk: (raw['stockOk'] ?? (order as Raw)['stockOk'] ?? true) as boolean,
    needs: str(raw['needs'], 'none') as AtelierOrderPayload['needs'],
    needsCopyKey: str(raw['needsCopyKey']),
    cutSheet: raw['cutSheet'] == null ? null : adaptCutSheet(raw['cutSheet'] as Raw, appendix),
    cutSheetFailed: raw['cutSheet'] == null,
    appendix: appendix.map((entry) => ({
      event: str(entry['event']),
      actorName: str(entry['actorDisplayName']),
      at: str(entry['at']),
    })),
    /* P-THAI-01's block sits BESIDE `order`, not inside it, so the spread above
       does not carry it. It stayed invisible for exactly that reason. */
    ...(raw['bilingual'] == null
      ? {}
      : { bilingual: raw['bilingual'] as AtelierOrderPayload['bilingual'] }),
    ...(raw['photographs'] == null ? {} : { photographs: raw['photographs'] as AtelierOrderPayload['photographs'] }),
    ...(raw['assignableStaff'] == null
      ? {}
      : { assignableStaff: raw['assignableStaff'] as AtelierOrderPayload['assignableStaff'] }),
    acknowledgements: (raw['acknowledgements'] ?? []) as AtelierOrderPayload['acknowledgements'],
    overrides: (raw['overrides'] ?? []) as AtelierOrderPayload['overrides'],
    boundRecord: bound == null ? null : (bound as unknown as AtelierOrderPayload['boundRecord']),
    // D-026(d): the house lead time is offered ONLY here, and only as a
    // one-action fill a tailor must actively press. Nothing derived from it
    // ever reaches a client screen.
    houseLeadTimeWindow:
      typeof raw['internalPlanningLeadTimeDays'] === 'number'
        ? houseWindowFrom(raw['internalPlanningLeadTimeDays'] as number)
        : null,
  };
}

function houseWindowFrom(days: number): { earliest: string; latest: string } {
  const day = 24 * 60 * 60 * 1000;
  return {
    earliest: new Date(Date.now() + days * day).toISOString(),
    latest: new Date(Date.now() + (days + 14) * day).toISOString(),
  };
}
